import { useMemo, useState, useEffect, useCallback } from "react";
import { type Property } from "@/types/property";
import type { PropertyContract } from "@/hooks/useContracts";
import { filterActiveContracts } from "@/utils/contractUtils";

type Filters = {
  propertyIds: string[];
  organizationIds: string[];
};

interface UseFilteredPropertiesProps {
  properties: Property[];
  contracts: PropertyContract[];
}

const useFilteredProperties = ({ properties, contracts }: UseFilteredPropertiesProps) => {
  const [filters, setFilters] = useState<Filters>({
    propertyIds: [],
    organizationIds: [],
  });

  // Calculate valid property IDs based on organization filter
  const validPropertyIds = useMemo(() => {
    if (filters.organizationIds.length === 0) return null; // null means "all valid"
    
    const activeContracts = filterActiveContracts(contracts);
    return new Set(
      activeContracts
        .filter(c => c.organization_id && filters.organizationIds.includes(c.organization_id))
        .map(c => c.property_id)
    );
  }, [contracts, filters.organizationIds]);

  // Sanitize propertyIds when organization filter changes
  useEffect(() => {
    if (validPropertyIds === null) return; // No org filter, don't sanitize
    
    const currentPropertyIds = filters.propertyIds;
    if (currentPropertyIds.length === 0) return; // Nothing to sanitize
    
    const sanitized = currentPropertyIds.filter(id => validPropertyIds.has(id));
    
    if (sanitized.length !== currentPropertyIds.length) {
      setFilters(prev => ({ ...prev, propertyIds: sanitized }));
    }
  }, [validPropertyIds, filters.propertyIds]);

  const handleFilterChange = <K extends keyof Filters>(
    key: K,
    value: Filters[K],
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filteredProperties = useMemo(() => {
    // First, filter out properties hidden from calendar
    let result = properties.filter(p => !p.hiddenFromCalendar);

    // Always filter to properties with active contracts
    const activeContracts = filterActiveContracts(contracts);
    const activePropertyIds = new Set(activeContracts.map(c => c.property_id));
    result = result.filter(p => activePropertyIds.has(p.id));

    // Filter by organizations
    if (filters.organizationIds.length > 0) {
      const propertyIdsInOrgs = new Set(
        activeContracts
          .filter((c) => c.organization_id && filters.organizationIds.includes(c.organization_id))
          .map((c) => c.property_id)
      );
      result = result.filter((p) => propertyIdsInOrgs.has(p.id));
    }

    // Filter by specific properties
    if (filters.propertyIds.length > 0) {
      result = result.filter((p) => filters.propertyIds.includes(p.id));
    }

    // Sort by sortOrder (ascending, nulls/999 last)
    result.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));

    return result;
  }, [properties, contracts, filters]);

  return {
    filters,
    handleFilterChange,
    filteredProperties,
  };
};

export default useFilteredProperties;
